<script setup>
import { computed, ref, watch } from "vue";
import {
  DateFormatter,
  getLocalTimeZone,
  parseDate,
  CalendarDate,
} from "@internationalized/date";
import {
  CalendarRoot,
  CalendarGrid,
  CalendarGridHead,
  CalendarGridBody,
  CalendarGridRow,
  CalendarHeadCell,
  CalendarPrev,
  CalendarNext,
} from "reka-ui";
import {
  CalendarCell,
  CalendarCellTrigger,
  CalendarGridRow as SCalendarGridRow,
} from "@/shared/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/shared/ui/popover";
import Button from "@/shared/ui/button/Button.vue";
import { cn } from "@/shared/lib/utils";
import { buttonVariants } from "@/shared/ui/button";

const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "Выберите дату",
  },
  editable: {
    type: Boolean,
    default: false,
  },
  yearRange: {
    type: Array,
    default: () => {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let y = currentYear; y >= 1900; y--) {
        years.push(y);
      }
      return years;
    },
  },
});

const emit = defineEmits(["update:modelValue"]);

const df = new DateFormatter("ru-RU", { dateStyle: "long" });

const months = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];

const placeholder = ref(new CalendarDate(new Date().getFullYear(), 1, 1));

const calendarValue = computed({
  get() {
    if (!props.modelValue) return undefined;
    try {
      return parseDate(props.modelValue);
    } catch {
      return undefined;
    }
  },
  set(val) {
    if (val) {
      emit("update:modelValue", val.toString());
    } else {
      emit("update:modelValue", "");
    }
  },
});

const displayValue = computed(() => {
  if (!calendarValue.value) return "";
  return df.format(calendarValue.value.toDate(getLocalTimeZone()));
});

function onMonthChange(e) {
  const month = Number(e.target.value);
  placeholder.value = placeholder.value.set({ month });
}

function onYearChange(e) {
  const year = Number(e.target.value);
  placeholder.value = placeholder.value.set({ year });
}

const inputText = ref(props.modelValue || "");

watch(
  () => props.modelValue,
  (val) => {
    inputText.value = val || "";
  }
);

function onInputChange(e) {
  const val = e.target.value;
  inputText.value = val;
  const trimmed = val.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    emit("update:modelValue", trimmed);
  } else if (!trimmed) {
    emit("update:modelValue", "");
  }
}

watch(calendarValue, (val) => {
  if (val) {
    placeholder.value = val.copy();
  }
});
</script>

<template>
  <Popover v-slot="{ close }">
    <!-- Editable mode: input + calendar icon trigger -->
    <template v-if="editable">
      <div class="relative w-full">
        <input
          :value="inputText"
          @input="onInputChange"
          :placeholder="props.placeholder"
          data-slot="input"
          :data-filled="modelValue ? '' : null"
          class="w-full h-10 rounded-md border border-input bg-background px-3 py-1 text-sm pr-9 focus-visible:border-primary outline-none"
        />
        <PopoverTrigger as-child>
          <button
            type="button"
            class="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="h-4 w-4"
            >
              <path d="M8 2v4" />
              <path d="M16 2v4" />
              <rect width="18" height="18" x="3" y="4" rx="2" />
              <path d="M3 10h18" />
            </svg>
          </button>
        </PopoverTrigger>
      </div>
    </template>

    <!-- Default mode: button trigger -->
    <PopoverTrigger v-else as-child>
      <Button
        variant="outline"
        :data-filled="modelValue ? '' : null"
        :class="
          cn(
            'w-full justify-start text-left font-normal h-10',
            !modelValue && 'text-muted-foreground'
          )
        "
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="mr-2 h-4 w-4 shrink-0"
        >
          <path d="M8 2v4" />
          <path d="M16 2v4" />
          <rect width="18" height="18" x="3" y="4" rx="2" />
          <path d="M3 10h18" />
        </svg>
        <span v-if="displayValue">{{ displayValue }}</span>
        <span v-else class="text-muted-foreground text-xs">{{
          props.placeholder
        }}</span>
      </Button>
    </PopoverTrigger>
    <PopoverContent class="w-auto p-0" align="start">
      <CalendarRoot
        v-slot="{ grid, weekDays }"
        v-model="calendarValue"
        v-model:placeholder="placeholder"
        locale="ru"
        class="p-3"
        initial-focus
        @update:model-value="close"
      >
        <!-- Header с select-ами для месяца и года -->
        <div class="flex items-center justify-between gap-2 pb-2">
          <CalendarPrev
            :class="
              cn(
                buttonVariants({ variant: 'outline' }),
                'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
              )
            "
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="h-4 w-4"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
          </CalendarPrev>

          <div class="flex items-center gap-1">
            <select
              :value="placeholder.month"
              class="appearance-none bg-transparent text-sm font-medium cursor-pointer hover:bg-accent rounded px-1.5 py-0.5 outline-none"
              @change="onMonthChange"
            >
              <option v-for="(m, i) in months" :key="i" :value="i + 1">
                {{ m }}
              </option>
            </select>

            <select
              :value="placeholder.year"
              class="appearance-none bg-transparent text-sm font-medium cursor-pointer hover:bg-accent rounded px-1.5 py-0.5 outline-none"
              @change="onYearChange"
            >
              <option v-for="y in yearRange" :key="y" :value="y">
                {{ y }}
              </option>
            </select>
          </div>

          <CalendarNext
            :class="
              cn(
                buttonVariants({ variant: 'outline' }),
                'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
              )
            "
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="h-4 w-4"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </CalendarNext>
        </div>

        <!-- Сетка календаря -->
        <div
          class="flex flex-col gap-y-4 mt-2 sm:flex-row sm:gap-x-4 sm:gap-y-0"
        >
          <CalendarGrid
            v-for="month in grid"
            :key="month.value.toString()"
            class="w-full border-collapse space-y-1"
          >
            <CalendarGridHead>
              <CalendarGridRow class="flex">
                <CalendarHeadCell
                  v-for="day in weekDays"
                  :key="day"
                  class="w-9 rounded-md text-[0.8rem] font-normal text-muted-foreground"
                >
                  {{ day }}
                </CalendarHeadCell>
              </CalendarGridRow>
            </CalendarGridHead>
            <CalendarGridBody>
              <SCalendarGridRow
                v-for="(weekDates, index) in month.rows"
                :key="`weekDate-${index}`"
                class="mt-2 w-full"
              >
                <CalendarCell
                  v-for="weekDate in weekDates"
                  :key="weekDate.toString()"
                  :date="weekDate"
                >
                  <CalendarCellTrigger :day="weekDate" :month="month.value" />
                </CalendarCell>
              </SCalendarGridRow>
            </CalendarGridBody>
          </CalendarGrid>
        </div>
      </CalendarRoot>
    </PopoverContent>
  </Popover>
</template>
